/**
 * Public event detail page.
 *
 * Route: /c/:chapterSlug/events/:eventSlug
 *
 * Renders the event metadata + agenda + the auto-created landing Page
 * (via spec 080's PageRenderer) + a registration button when the event's
 * registrationMode allows it.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { useScope } from '@/contexts/ScopeContext';
import {
  ApiClientError,
  eventsApi,
  pagesApi,
  type EventV2,
  type PageDetail,
} from '@/services/api';
import { PageRenderer } from '@/components/pages/PageRenderer';

export function EventDetailPage() {
  const { eventSlug } = useParams();
  const { chapter, branch, loading: scopeLoading } = useScope();
  const { isAuthenticated } = useAuth();
  const [event, setEvent] = useState<EventV2 | null>(null);
  const [page, setPage] = useState<PageDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [registering, setRegistering] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [regError, setRegError] = useState<string | null>(null);

  useEffect(() => {
    if (scopeLoading || !chapter || !eventSlug) return;
    setError(null);
    const target = branch
      ? { scopeType: 'branch' as const, scopeId: branch.id }
      : { scopeType: 'chapter' as const, scopeId: chapter.id };
    eventsApi
      .getBySlug(target.scopeType, target.scopeId, eventSlug)
      .then((ev) => {
        setEvent(ev);
        if (ev.landingPageId) {
          return pagesApi.getById(ev.landingPageId).then(setPage).catch(() => undefined);
        }
        return undefined;
      })
      .catch((err: Error) => setError(err.message));
  }, [scopeLoading, chapter?.id, branch?.id, eventSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleRegister() {
    if (!event) return;
    setRegistering(true);
    setRegError(null);
    try {
      await eventsApi.register(event.id);
      setRegistered(true);
    } catch (err) {
      if (err instanceof ApiClientError) setRegError(err.message);
      else setRegError('Failed to register');
    } finally {
      setRegistering(false);
    }
  }

  if (scopeLoading || (!event && !error)) {
    return <div className="container mx-auto px-4 py-12 text-white/60">Loading…</div>;
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h1 className="text-3xl font-display font-bold text-white mb-2">Event not found</h1>
        <p className="text-white/60">{error}</p>
      </div>
    );
  }

  if (!event) return null;

  const canRegister = event.registrationMode !== 'none';

  return (
    <div className="container mx-auto px-4 py-12">
      <header className="mb-6">
        <h1 className="text-4xl font-display font-bold text-white">{event.title}</h1>
        <p className="text-white/60 mt-1">
          {new Date(event.startsAt).toLocaleString()}
          {event.endsAt ? ` – ${new Date(event.endsAt).toLocaleString()}` : ''}
          {event.locationText ? ` · ${event.locationText}` : ''}
        </p>
      </header>

      {canRegister && (
        <div className="card mb-8">
          {registered ? (
            <p className="text-green-400">You are registered for this event.</p>
          ) : !isAuthenticated ? (
            <p className="text-white/70 text-sm">Sign in to register.</p>
          ) : (
            <>
              <button type="button" onClick={handleRegister} disabled={registering} className="btn-primary">
                {registering ? 'Registering…' : 'Register'}
              </button>
              {regError && <p className="text-red-400 text-sm mt-2">{regError}</p>}
            </>
          )}
        </div>
      )}

      {event.agenda && event.agenda.length > 0 && (
        <section className="card mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Agenda</h2>
          <ul className="space-y-2">
            {event.agenda.map((a, i) => (
              <li key={i} className="flex gap-4 text-white/80">
                <span className="font-mono text-white/60 w-20 shrink-0">{a.time}</span>
                <span>
                  <span className="font-semibold">{a.title}</span>
                  {a.description && <span className="text-white/60"> — {a.description}</span>}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {page && <PageRenderer page={page} />}
    </div>
  );
}

export default EventDetailPage;
